// Pure item defs, recipes, crafting + gathering rules. No Pixi, no DOM. (TECHNICAL_DESIGN.md §2.)
import { FOOD } from './const';
import { gatherMult } from './stats';
import type { FoodBuff, GearItem, ItemDef, ItemId, NodeKind, Slot } from './types';

export const ITEMS: Record<ItemId, ItemDef> = {
  wood: { id: 'wood', name: 'Wood', icon: '🪵', kind: 'resource', stack: 999 },
  fiber: { id: 'fiber', name: 'Fiber', icon: '🌾', kind: 'resource', stack: 999 },
  stone: { id: 'stone', name: 'Stone', icon: '🪨', kind: 'resource', stack: 999 },
  iron_ore: { id: 'iron_ore', name: 'Iron Ore', icon: '⛏️', kind: 'resource', stack: 999 },
  gold_ore: { id: 'gold_ore', name: 'Gold Ore', icon: '🟡', kind: 'resource', stack: 999 },
  iron_bar: { id: 'iron_bar', name: 'Iron Bar', icon: '🔩', kind: 'resource', stack: 999 },
  hide: { id: 'hide', name: 'Hide', icon: '🟫', kind: 'resource', stack: 999 },
  relic: { id: 'relic', name: 'Grove Relic', icon: '⭐', kind: 'resource', stack: 9 },
  berries: { id: 'berries', name: 'Berries', icon: '🫐', kind: 'food', stack: 99, food: FOOD.berries },
  cooked_meat: { id: 'cooked_meat', name: 'Cooked Meat', icon: '🍖', kind: 'food', stack: 99, food: FOOD.cooked_meat },
  raw_meat: { id: 'raw_meat', name: 'Raw Meat', icon: '🥩', kind: 'food', stack: 99, food: { maxHp: 8, spdPct: 0, durationS: 120 } },
  // placeables (crafted into inventory, then placed)
  campfire: { id: 'campfire', name: 'Campfire', icon: '🔥', kind: 'placeable', stack: 99 },
  torch: { id: 'torch', name: 'Torch', icon: '🕯️', kind: 'placeable', stack: 99 },
  wooden_wall: { id: 'wooden_wall', name: 'Wooden Wall', icon: '🧱', kind: 'placeable', stack: 99 },
  home_portal: { id: 'home_portal', name: 'Home Portal', icon: '🌀', kind: 'placeable', stack: 99 },
};

export type RecipeOut =
  | { kind: 'item'; item: ItemId; n: number }
  | { kind: 'tool'; tool: 'axe' | 'pickaxe'; tier: 1 | 2 | 3 }
  | { kind: 'weapon'; slot: 'melee' | 'bow'; tier: 1 | 2 }
  | { kind: 'armor'; tier: 1 | 2 };

export interface Recipe {
  id: string;
  name: string;
  icon: string;
  needs: Partial<Record<ItemId, number>>;
  out: RecipeOut;
  near?: 'campfire';
}

export const RECIPES: Recipe[] = [
  { id: 'wood_axe', name: 'Wood Axe', icon: '🪓', needs: { wood: 5, fiber: 2 }, out: { kind: 'tool', tool: 'axe', tier: 1 } },
  { id: 'wood_pickaxe', name: 'Wood Pickaxe', icon: '⛏️', needs: { wood: 5, fiber: 2 }, out: { kind: 'tool', tool: 'pickaxe', tier: 1 } },
  { id: 'campfire', name: 'Campfire', icon: '🔥', needs: { wood: 8, stone: 3 }, out: { kind: 'item', item: 'campfire', n: 1 } },
  { id: 'torch', name: 'Torch ×2', icon: '🕯️', needs: { wood: 2, fiber: 1 }, out: { kind: 'item', item: 'torch', n: 2 } },
  { id: 'wooden_wall', name: 'Wooden Wall ×2', icon: '🧱', needs: { wood: 4 }, out: { kind: 'item', item: 'wooden_wall', n: 2 } },
  { id: 'home_portal', name: 'Home Portal', icon: '🌀', needs: { wood: 12, stone: 10, iron_bar: 3, relic: 1 }, out: { kind: 'item', item: 'home_portal', n: 1 }, near: 'campfire' },
  { id: 'stone_axe', name: 'Stone Axe', icon: '🪓', needs: { wood: 3, stone: 5 }, out: { kind: 'tool', tool: 'axe', tier: 2 } },
  { id: 'stone_pickaxe', name: 'Stone Pickaxe', icon: '⛏️', needs: { wood: 3, stone: 5 }, out: { kind: 'tool', tool: 'pickaxe', tier: 2 } },
  { id: 'stone_sword', name: 'Stone Sword', icon: '🗡️', needs: { wood: 2, stone: 4 }, out: { kind: 'weapon', slot: 'melee', tier: 1 } },
  { id: 'wood_bow', name: 'Wood Bow', icon: '🏹', needs: { wood: 4, fiber: 3 }, out: { kind: 'weapon', slot: 'bow', tier: 1 } },
  { id: 'leather_armor', name: 'Leather Armor', icon: '🦺', needs: { hide: 5 }, out: { kind: 'armor', tier: 1 } },
  { id: 'cooked_meat', name: 'Cook Meat', icon: '🍖', needs: { raw_meat: 1 }, out: { kind: 'item', item: 'cooked_meat', n: 1 }, near: 'campfire' },
  { id: 'iron_bar', name: 'Iron Bar', icon: '🔩', needs: { iron_ore: 2 }, out: { kind: 'item', item: 'iron_bar', n: 1 }, near: 'campfire' },
  { id: 'iron_axe', name: 'Iron Axe', icon: '🪓', needs: { wood: 3, iron_bar: 3 }, out: { kind: 'tool', tool: 'axe', tier: 3 } },
  { id: 'iron_pickaxe', name: 'Iron Pickaxe', icon: '⛏️', needs: { wood: 3, iron_bar: 3 }, out: { kind: 'tool', tool: 'pickaxe', tier: 3 } },
  { id: 'iron_sword', name: 'Iron Sword', icon: '⚔️', needs: { wood: 2, iron_bar: 4 }, out: { kind: 'weapon', slot: 'melee', tier: 2 } },
  { id: 'iron_bow', name: 'Iron Bow', icon: '🏹', needs: { wood: 2, iron_bar: 2 }, out: { kind: 'weapon', slot: 'bow', tier: 2 } },
  { id: 'iron_armor', name: 'Iron Armor', icon: '🛡️', needs: { iron_bar: 5, hide: 2 }, out: { kind: 'armor', tier: 2 } },
];

export function recipeById(id: string): Recipe | undefined {
  return RECIPES.find((r) => r.id === id);
}

// Crafted weapon/bow/armor tiers → a common-rarity GearItem (uid minted by the caller). Weapon→
// weapon slot, bow→subweapon slot, armor→armor slot. Lets crafted gear equip exactly like loot.
const CRAFT_WEAPON_ATK = [0, 8, 16];  // tier 1/2 melee
const CRAFT_BOW_ATK = [0, 6, 12];     // tier 1/2 bow
const CRAFT_ARMOR_DEF = [0, 4, 10];   // tier 1/2 armor
export function makeCraftedGear(recipeId: string): Omit<GearItem, 'uid'> | null {
  const r = recipeById(recipeId);
  if (!r) return null;
  const o = r.out;
  const common = { name: r.name, rarity: 0 as const, affixes: [], empower: { level: 0, bumps: {} }, floor: 0, crafted: recipeId, icon: r.icon };
  if (o.kind === 'weapon') {
    const slot: Slot = o.slot === 'bow' ? 'subweapon' : 'weapon';
    const atk = (o.slot === 'bow' ? CRAFT_BOW_ATK : CRAFT_WEAPON_ATK)[o.tier] ?? 0;
    return { ...common, slot, base: { atk } };
  }
  if (o.kind === 'armor') return { ...common, slot: 'armor', base: { def: CRAFT_ARMOR_DEF[o.tier] ?? 0 } };
  return null;
}

/** Pure: can this recipe be crafted, given inventory and campfire proximity? */
export function canCraft(recipe: Recipe, inv: Record<ItemId, number>, nearCampfire: boolean): boolean {
  if (recipe.near === 'campfire' && !nearCampfire) return false;
  for (const [item, n] of Object.entries(recipe.needs)) if ((inv[item] ?? 0) < (n ?? 0)) return false;
  return true;
}

// ---- gathering: which pickaxe tier a node needs, and gather power ----
export function requiredPickaxe(kind: NodeKind): number {
  switch (kind) {
    case 'stone': return 1;
    case 'iron': return 2;
    case 'gold': return 3;
    default: return 0; // trees/bushes need no tool
  }
}

/** Gather power (node HP removed per interact) from the right tool tier + use-skill level. */
export function gatherPower(kind: NodeKind, axeTier: number, pickaxeTier: number, woodXp: number, mineXp: number): number {
  if (kind === 'tree' || kind === 'bush') return (1 + axeTier) * gatherMult(woodXp); // hand=1
  return pickaxeTier * gatherMult(mineXp); // requires a pickaxe (caller checks requiredPickaxe)
}

export function nodeYield(kind: NodeKind): { item: ItemId; n: number }[] {
  switch (kind) {
    case 'tree': return [{ item: 'wood', n: 3 }, { item: 'fiber', n: 1 }];
    case 'bush': return [{ item: 'berries', n: 2 }, { item: 'fiber', n: 1 }];
    case 'stone': return [{ item: 'stone', n: 3 }];
    case 'iron': return [{ item: 'iron_ore', n: 2 }];
    case 'gold': return [{ item: 'gold_ore', n: 1 }];
    default: return [];
  }
}

export function makeFoodBuff(itemId: ItemId, nowGameS: number): FoodBuff | null {
  const def = ITEMS[itemId];
  if (!def?.food) return null;
  return { itemId, maxHp: def.food.maxHp ?? 0, spdPct: def.food.spdPct ?? 0, expiresAtGameS: nowGameS + def.food.durationS };
}

export function resolveFood(buffs: FoodBuff[], nowGameS: number, maxSlots: number): { active: FoodBuff[]; maxHpBonus: number; spdPct: number } {
  const alive = buffs.filter((b) => b.expiresAtGameS > nowGameS).sort((a, b) => b.expiresAtGameS - a.expiresAtGameS).slice(0, maxSlots);
  let maxHpBonus = 0, spdPct = 0;
  for (const b of alive) { maxHpBonus += b.maxHp; spdPct += b.spdPct; }
  return { active: alive, maxHpBonus, spdPct };
}
